// src/components/HUD/HUDScriptManager.js
// UPDATED: 2026-08-09 — all endpoints moved to /hud/* so hudToken works fully.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
const BUILT_IN = ['q3_unit_months', 'rpt04_delta', 'cfp_bookkeeping', 'status_summary'];
export default function HUDScriptManager({ onScriptsChanged }) {
  const [expanded,       setExpanded]       = useState(false);
  const [scripts,        setScripts]        = useState([]);
  const [loadingList,    setLoadingList]    = useState(false);
  const [scriptName,     setScriptName]     = useState('');
  const [scriptContent,  setScriptContent]  = useState('');
  const [outputFileName, setOutputFileName] = useState('');
  const [saving,         setSaving]         = useState(false);
  const [result,         setResult]         = useState(null);
  const [removing,       setRemoving]       = useState(null);

  const fetchScripts = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/hud/list-scripts`, { withCredentials: true });
      setScripts(res.data.scripts || []);
      if (onScriptsChanged) onScriptsChanged(res.data.scripts || []);
    } catch (err) {
      console.error('[HUDScriptManager] Failed to fetch scripts:', err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { if (expanded) fetchScripts(); }, [expanded]);

  const handleSave = async () => {
    setResult(null);
    if (!scriptName.trim())    { setResult({ type: 'error', message: 'Script name is required.' }); return; }
    if (!scriptContent.trim()) { setResult({ type: 'error', message: 'Script content is required.' }); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(scriptName)) { setResult({ type: 'error', message: 'Script name must be alphanumeric and underscores only.' }); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/hud/register-script`,
        { scriptName: scriptName.trim(), scriptContent: scriptContent.trim(), outputFileName: outputFileName.trim() || scriptName.trim() },
        { withCredentials: true }
      );
      if (res.data.success) {
        setResult({ type: 'success', message: scriptName + ' registered successfully. It will appear as a new Run ACL card.' });
        setScriptName(''); setScriptContent(''); setOutputFileName('');
        await fetchScripts();
      } else {
        setResult({ type: 'error', message: res.data.error || 'Registration failed.' });
      }
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (name) => {
    if (!window.confirm('Remove script "' + name + '"? This cannot be undone.')) return;
    setRemoving(name);
    try {
      await axios.delete(`${API_URL}/hud/remove-script/${name}`, { withCredentials: true });
      await fetchScripts();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #D3D1C7', borderRadius: '10px', marginBottom: '20px', overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '14px 20px', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: expanded ? '#F7F6F2' : '#fff',
          borderBottom: expanded ? '1px solid #E2E0D8' : 'none',
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1a1a18' }}>⚙️ ACL Script Manager</p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#888780' }}>Register new business rule scripts directly from the dashboard</p>
        </div>
        <span style={{ fontSize: '13px', color: '#888780' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '600', color: '#5F5E5A' }}>
              Registered Scripts {loadingList ? '(loading...)' : '(' + scripts.length + ')'}
            </p>
            {scripts.length === 0 && !loadingList && (
              <p style={{ fontSize: '11px', color: '#888780', margin: 0 }}>No scripts registered yet.</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {scripts.map(s => {
                const isBuiltIn = BUILT_IN.includes(s.scriptName);
                return (
                  <div key={s.scriptName} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '5px 12px', borderRadius: '20px', fontSize: '11px',
                    background: isBuiltIn ? '#EEF2FF' : '#E1F5EE',
                    border: '1px solid ' + (isBuiltIn ? '#C4C2D8' : '#1D9E75'),
                    color: isBuiltIn ? '#3C3489' : '#085041',
                  }}>
                    <span style={{ fontWeight: '600' }}>{s.scriptName}</span>
                    {s.outputFileName !== s.scriptName && <span style={{ opacity: 0.65 }}>→ {s.outputFileName}</span>}
                    {isBuiltIn && <span style={{ fontSize: '10px', opacity: 0.6 }}>built-in</span>}
                    {!isBuiltIn && (
                      <button
                        onClick={() => handleRemove(s.scriptName)}
                        disabled={removing === s.scriptName}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A32D2D', fontSize: '12px', padding: '0 2px', fontWeight: '700', lineHeight: 1 }}
                      >
                        {removing === s.scriptName ? '...' : '×'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #E2E0D8', paddingTop: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: '600', color: '#5F5E5A' }}>Register New Script</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: '#5F5E5A', display: 'block', marginBottom: '4px' }}>
                  Script Name <span style={{ color: '#A32D2D' }}>*</span>
                </label>
                <input
                  value={scriptName}
                  onChange={e => setScriptName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                  placeholder="e.g. cash_balance_check"
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: '1px solid #D3D1C7', borderRadius: '6px', boxSizing: 'border-box', fontFamily: 'monospace' }}
                />
                <p style={{ margin: '3px 0 0', fontSize: '10px', color: '#888780' }}>Alphanumeric and underscores only.</p>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: '600', color: '#5F5E5A', display: 'block', marginBottom: '4px' }}>Output CSV Filename</label>
                <input
                  value={outputFileName}
                  onChange={e => setOutputFileName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                  placeholder={scriptName || 'leave blank to use script name'}
                  style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: '1px solid #D3D1C7', borderRadius: '6px', boxSizing: 'border-box', fontFamily: 'monospace' }}
                />
                <p style={{ margin: '3px 0 0', fontSize: '10px', color: '#888780' }}>Without .csv. Leave blank to match script name.</p>
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#5F5E5A', display: 'block', marginBottom: '4px' }}>
                ACL Script Content <span style={{ color: '#A32D2D' }}>*</span>
              </label>
              <textarea
                value={scriptContent}
                onChange={e => setScriptContent(e.target.value)}
                placeholder={'SET SAFETY OFF\nOPEN "C:\\HUD\\working\\FDS_Data"\n...\nQUIT'}
                rows={12}
                style={{ width: '100%', padding: '10px', fontSize: '11px', border: '1px solid #D3D1C7', borderRadius: '6px', boxSizing: 'border-box', fontFamily: 'monospace', resize: 'vertical', lineHeight: '1.5', background: '#FAFAF8' }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#888780' }}>Paste your raw ACL script. Make sure it starts with SET SAFETY OFF and ends with QUIT.</p>
            </div>
            {result && (
              <div style={{
                padding: '10px 14px', borderRadius: '6px', fontSize: '12px', marginBottom: '12px',
                background: result.type === 'success' ? '#E1F5EE' : '#FCEBEB',
                color: result.type === 'success' ? '#085041' : '#A32D2D',
                border: '1px solid ' + (result.type === 'success' ? '#1D9E75' : '#D85A30'),
              }}>
                {result.type === 'success' ? '✓ ' : '✗ '}{result.message}
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? '#C4C2D8' : '#534AB7', color: '#fff', border: 'none',
                borderRadius: '7px', padding: '10px 24px', fontSize: '13px', fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Registering... (this may take 30-60 seconds)' : 'Save & Register Script'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}